import { useEffect, useRef } from "react";

import { graphql, useFragment } from "react-relay";

import { parseColorScheme, useTheme } from "app/contexts/ThemeContext"; // avoiding barrel export to satisfy relay

import type { ColorSchemeSync_query$key } from "./__generated__/ColorSchemeSync_query.graphql";

/** Props for the ColorSchemeSync component */
interface Props {
  /** Bootstrap query ref carrying the saved color scheme `userData`. */
  queryRef: ColorSchemeSync_query$key;
}

/**
 * Reconciles the locally-cached color scheme with the value persisted to
 * `userData`. The server is the cross-device source of truth, so on load its
 * value wins and is written back to localStorage. Because every local change is
 * also persisted server-side, the server value is always at least as fresh as
 * the last change made on any device. Renders nothing.
 *
 * The color scheme rides along with the bootstrap `AppQuery` (see its
 * `...ColorSchemeSync_query` spread) rather than issuing its own request, so it
 * costs no extra round-trip and cannot fail independently of the app load.
 */
export function ColorSchemeSync({ queryRef }: Props) {
  const { colorScheme, setColorScheme } = useTheme();
  const hasSynced = useRef(false);

  const data = useFragment(
    graphql`
      fragment ColorSchemeSync_query on Query {
        colorScheme: userData(
          filterElement: {
            single: { name: "name", operator: "equals", value: "color_scheme" }
          }
        ) {
          edges {
            node {
              value
            }
          }
        }
      }
    `,
    queryRef,
  );

  const serverScheme = parseColorScheme(data.colorScheme.edges[0]?.node.value);

  useEffect(() => {
    if (hasSynced.current || serverScheme === null) {
      return;
    }
    hasSynced.current = true;
    if (serverScheme !== colorScheme) {
      setColorScheme(serverScheme);
    }
  }, [serverScheme, colorScheme, setColorScheme]);

  return null;
}
